import { Component, EventEmitter, Input, NgZone, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SurveySchema } from '@queries/generated';
import { ensurePresence } from '@rx/ensure-presence';
import { safeReplay } from '@rx/safe-replay';
import { CurrentUserService } from '@services/current-user.service';
import { SurveysService } from '@services/surveys.service';
import { GlobalID } from '@util/global-id';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Model } from 'survey-core';

class SurveyDisplayRecord {
  // whether the survey has been displayed to the user
  displayed = false;

  // whether the user has completed the survey
  completed = false;

  // whether the user has opted out of the survey
  optedOut = false;

  // the random number used to determine if the survey should be displayed
  rand = Math.random();

  constructor(obj: Partial<SurveyDisplayRecord>) {
    Object.assign(this, obj);
  }

  shouldDisplay(percentage: number) {
    if (this.optedOut || this.completed) {
      return false;
    }

    return this.rand < percentage;
  }
}

@UntilDestroy()
@Component({
  selector: 'vnm-display-survey',
  templateUrl: 'display-survey.component.html',
  styleUrls: ['./display-survey.component.scss']
})
export class DisplaySurveyComponent implements OnInit {
  // allow passing in a GID or integer ID

  // SurveySchema integer ID
  @Input() surveyID: number;

  // SurveySchema GID
  @Input() surveyGID: string;

  // percentage chance of displaying the survey
  @Input() displayPercentage: number;

  // whether the survey can be closed
  @Input() closable = true;

  // the survey trigger event to listen for to display the survey
  @Input() event: string;

  visible = false;
  completing = false;
  memoryName: string;
  surveySchema$: Observable<SurveySchema>;
  surveyModel$: Observable<Model>;
  surveyDisplayRecord$: Observable<SurveyDisplayRecord>;
  surveyDisplayRecordChanges$ = new EventEmitter<Partial<SurveyDisplayRecord>>();

  constructor(
    private surveysService: SurveysService,
    private currentUserService: CurrentUserService,
    private zone: NgZone
  ) {}

  ngOnInit() {
    // build a GID if we were given an integer ID
    if (this.surveyID) {
      this.surveyGID = new GlobalID({ model: 'SurveySchema', id: this.surveyID }).toString();
    }

    // find our survey
    this.surveySchema$ = this.surveysService.find(this.surveyGID).pipe(
      catchError((error) => {
        return of<SurveySchema>(null);
      }),
      ensurePresence(),
      safeReplay()
    );

    // turn the survey schema into a Model for the surveyjs library
    this.surveyModel$ = this.surveySchema$.pipe(
      withLatestFrom(this.currentUserService.user$),
      map(([schema, user]) => {
        const model = new Model(schema.content);
        model.onComplete.add((survey) => {
          this.zone.run(() => {
            this.completing = true;
            survey.setValue('userId', user.id);
            this.surveysService
              .createResult({
                surveySchemaId: this.surveyGID,
                surveyResult: { content: survey.data }
              })
              .subscribe(() => {
                this.visible = false;
                this.surveyDisplayRecordChanges$.emit({ completed: true });
              });
          });
        });
        return model;
      })
    );

    // load the survey display record from user memory
    this.surveyDisplayRecord$ = this.surveySchema$.pipe(
      switchMap((schema) => {
        this.memoryName = ['survey', schema.id].join('.');
        return this.currentUserService.memory(this.memoryName);
      }),
      map((memory) => {
        if (memory) {
          return new SurveyDisplayRecord(JSON.parse(memory.value));
        }

        return new SurveyDisplayRecord({});
      }),
      distinctUntilChanged()
    );

    // watch for survey display record changes and persist them to user memory
    this.surveyDisplayRecordChanges$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.surveyDisplayRecord$),
        switchMap(([changes, record]) => {
          const memory = JSON.stringify(Object.assign({}, record, changes));
          return this.currentUserService.remember(this.memoryName, memory);
        })
      )
      .subscribe();

    if (this.event) {
      this.surveysService
        .listen(this.event)
        .pipe(
          switchMap(() => this.surveyDisplayRecord$),
          take(1)
        )
        .subscribe((record) => {
          this.visible = record.shouldDisplay(this.displayPercentage);
          if (this.visible) {
            this.surveyDisplayRecordChanges$.emit({ displayed: true });
          }
        });
    } else {
      // initial load to decide whether to display the survey
      this.surveyDisplayRecord$.pipe(take(1)).subscribe((record) => {
        this.visible = record.shouldDisplay(this.displayPercentage);
        if (this.visible) {
          this.surveyDisplayRecordChanges$.emit({ displayed: true });
        }
      });
    }
  }

  onDialogHide() {
    if (!this.completing) {
      this.surveyDisplayRecordChanges$.emit({ optedOut: true });
    }
  }
}
